import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback, useMemo, useRef, type SetStateAction } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  ChevronRight,
  MessageCircle,
  Package,
  Scale,
  ShoppingCart,
  Upload,
  X,
} from "lucide-react";
import { GARMENT_SVGS, type GarmentSvgProps } from "@/components/merchant/garment-svgs";
import { matchesSearch } from "@/lib/normalize-search";
import { cartRequiresShipping, productTypeRequiresShipping } from "@structura/core/digital-assets";
import { DEFAULT_PRODUCT_SORT, sortProducts, type ProductSortKey } from "@/lib/product-sort";
import { MarketTrackingScripts } from "@/components/MarketTrackingScripts";
import { filterPaymentMethodsForDelivery } from "@/lib/market-order-validate";
import {
  buildCheckoutPickupLocations,
  defaultCheckoutPickupLocationId,
  resolveCheckoutMetodEntregaConfig,
  resolveOrderPickupLocationId,
} from "@/lib/checkout-pickup";
import { formatPesos as pesos } from "@/lib/market-format";
import { marketHasActiveFilters } from "@/components/market/MarketCatalogToolbar";
import { MarketEngagementOverlays } from "@/components/market/MarketEngagementOverlays";
import { MarketStoreHeader, MarketFloatingCartBar } from "@/components/market/MarketChrome";
import { MarketAnnouncementBar } from "@/components/market/MarketAnnouncementBar";
import { projectExperience } from "@/lib/experience-definition/project";
import { MarketCategorySidebar } from "@/components/market/MarketCategoryNav";
import { MarketFooter } from "@/components/market/MarketFooter";
import { MarketBottomNav } from "@/components/market/MarketBottomNav";
import { MarketCustomerAccountBanner } from "@/components/market/MarketCustomerAccountBanner";
import { cartLineKey, cartLineQtyLimit, cartBundleDiscountTotal } from "@/lib/market-cart-storage";
import { showAddedToCart } from "@/lib/market-added-to-cart";
import { useStorefrontTheme } from "@/components/market/useStorefrontTheme";
import { usePersistedMarketCart } from "@/hooks/use-persisted-market-cart";
import { trackAddToCart, trackRemoveFromCart } from "@/lib/storefront-funnel";
import {
  ProductAddonsSheet,
  productNeedsAddonPrompt,
  type AddonConfirmPayload,
} from "@/components/market/ProductAddonsSheet";
import {
  customerAuthHeaders,
  getCustomerProfile,
  getCustomerToken,
  customerFetch,
} from "@/lib/market-customer-auth";
import { normalizedAddressFromDb } from "@structura/core/address";
import {
  buildCheckoutPaymentOptions,
  checkoutPaymentLabel,
  isCheckoutOnlinePayment,
} from "@/lib/storefront-checkout-payments";
import {
  MarketCheckoutForm,
  type MarketCheckoutFieldErrors,
  type MarketCheckoutPrefill,
} from "@/components/market/MarketCheckoutForm";
import { MarketFulfillmentStatusBanner } from "@/components/market/MarketFulfillmentStatusBanner";
import { useMarketAvailability } from "@/hooks/use-market-availability";
import { toast } from "sonner";
import { MarketCartItemRow, marketLineUnitCents } from "@/components/market/MarketCartItemRow";
import { MarketCheckoutDiscounts } from "@/components/market/MarketCheckoutDiscounts";
import { computeOrderTotal, MarketOrderTotals } from "@/components/market/MarketOrderSummary";
import type { TaxBreakdownTotals } from "@/components/tax/TaxBreakdown";
import {
  loadMarketCatalog,
  type MarketCatalogData,
  type MarketCatalogResult,
  type MarketLocation,
  type MarketProduct,
  type MarketStorefrontConfig,
} from "@/lib/market-catalog";
import { isGiftCardProduct } from "@/lib/gift-card-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { getRequest } from "@tanstack/react-start/server";
import { catalogPath, landingPath } from "@/lib/market-landing";
import { resolveTenantShareImage, tenantBrandIconUrl } from "@/lib/tenant-brand-image";
import {
  DEFAULT_CATEGORY_EXPERIENCE,
  filterCategoriesForNav,
  type CategoryExperience,
} from "@/lib/category-experience";
import { slugifyName } from "@/lib/slug";
import {
  StorefrontRuntime,
  PROMO_FILTER,
  EMPTY_STOREFRONT_COMPOSITION,
} from "@/components/market/runtime/StorefrontRuntime";
import type { StorefrontOffer, StorefrontRuntimeContext } from "@/components/market/runtime/types";
import { MarketHomeSkeleton } from "@/components/market/MarketSkeletons";
import { storefrontPath } from "@/lib/tenant-host-paths";
import { canonicalStorefrontSlug } from "@/lib/storefront-slug-aliases";

const fetchMarketCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    const request = getRequest();
    return loadMarketCatalog(slug, { request });
  });

export const Route = createFileRoute("/market/$slug")({
  pendingComponent: MarketHomeSkeleton,
  pendingMs: 0,
  validateSearch: (search: Record<string, unknown>) => {
    const out: { table?: string; session?: string; mesa?: string; cat?: string; marca?: string } = {};
    if (typeof search.table === "string") out.table = search.table;
    if (typeof search.session === "string") out.session = search.session;
    if (typeof search.mesa === "string") out.mesa = search.mesa;
    if (typeof search.cat === "string") out.cat = search.cat;
    if (typeof search.marca === "string") out.marca = search.marca;
    return out;
  },
  beforeLoad: ({ params, search }) => {
    const canonical = canonicalStorefrontSlug(params.slug);
    if (canonical !== params.slug) {
      throw redirect({
        to: "/market/$slug",
        params: { slug: canonical },
        search,
        replace: true,
      });
    }
    // QR legacy → experiencia mesa mobile-first
    if (typeof search.table === "string" && search.table && typeof search.session === "string" && search.session) {
      throw redirect({
        to: "/market/$slug/mesa",
        params: { slug: params.slug },
        search: {
          table: search.table,
          session: search.session,
          mesa: typeof search.mesa === "string" && search.mesa ? search.mesa : "Mesa",
        },
        replace: true,
      });
    }
  },
  loader: async ({ params }) => fetchMarketCatalog({ data: params.slug }),
  component: StorefrontPage,
  head: ({ loaderData, params }) => {
    const _ld = loaderData as MarketCatalogResult | undefined;
    const d = _ld?.ok ? _ld.data : null;
    const seo = d?.config?.seo;
    const title = seo?.title?.trim() || d?.tenant?.name || params.slug;
    const description =
      seo?.description?.trim() ||
      d?.config?.heroSubtitle ||
      d?.tenant?.description ||
      `Tienda online de ${title}`;
    const canonicalUrl = d?.tenant?.customDomain
      ? `https://${d.tenant.customDomain}`
      : `https://${d?.tenant?.subdomain || d?.tenant?.slug || params.slug}.structura.com.ar`;
    const origin = canonicalUrl.replace(/\/$/, "");
    const productImgs = (d?.products ?? [])
      .map((p: Product) => p.imagenUrl)
      .filter(Boolean)
      .slice(0, 8) as string[];
    const image = resolveTenantShareImage({
      seoOgImageUrl: seo?.ogImageUrl,
      logoUrl: d?.tenant?.logoUrl,
      heroImageUrl: d?.config?.heroImageUrl,
      productImageUrls: productImgs,
      slug: params.slug,
      origin,
    });
    const iconHref = d?.tenant?.logoUrl?.trim() || tenantBrandIconUrl(params.slug, origin);
    const heading = d?.experiencePublished?.brand?.typography?.heading?.family?.replace(/ /g, "+");
    const body = d?.experiencePublished?.brand?.typography?.body?.family?.replace(/ /g, "+");
    const fontFamilies = [heading, body].filter(Boolean) as string[];
    const fontHref = fontFamilies.length
      ? `https://fonts.googleapis.com/css2?${fontFamilies
          .map((f) =>
            /Cormorant|Playfair|Fraunces|Literata/i.test(f.replace(/\+/g, " "))
              ? `family=${f}:ital,wght@0,400;0,600;0,700;1,400;1,600`
              : `family=${f}:wght@400;500;600;700;800`,
          )
          .join("&")}&display=swap`
      : "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap";
    const links: Array<Record<string, string>> = [
      { rel: "manifest", href: `/api/manifest/${params.slug}` },
      { rel: "icon", href: iconHref },
      { rel: "apple-touch-icon", href: iconHref },
      { rel: "stylesheet", href: fontHref },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "author", content: title },
        { property: "og:site_name", content: title },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: image },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "apple-mobile-web-app-title", content: title },
      ],
      links,
    };
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type Category = MarketCatalogData["categories"][number];
type Product = MarketProduct;
type Location = MarketLocation;
type StorefrontConfig = MarketStorefrontConfig;
type CatalogData = MarketCatalogData;

interface BankTransferInfo {
  holder: string | null;
  cbu: string | null;
  alias: string | null;
  bank: string | null;
  notes: string | null;
}

interface CartItem {
  product: Product;
  qty: number; // for unidad: integer; for peso: kg (e.g. 0.5)
  notes?: string;
  variantId?: string;
  variantName?: string;
  variantPrecioUnitario?: number | null;
  variantPrecioPorKg?: number | null;
  addons?: Array<{ id: string; addonProductId: string; name: string; qty: number; precioUnitario: number }>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function itemAddonsPerUnit(item: CartItem): number {
  return (item.addons ?? []).reduce((s, a) => s + a.precioUnitario * a.qty, 0);
}

function itemTotal(item: CartItem): number {
  const addons = itemAddonsPerUnit(item);
  if (item.product.tipoVenta === "peso") {
    const base = item.variantPrecioPorKg ?? item.product.precioPorKg ?? 0;
    return (base + addons) * item.qty;
  }
  const base = item.variantPrecioUnitario ?? item.product.precioUnitario ?? 0;
  return (base + addons) * item.qty;
}

function cartTotal(cart: CartItem[]): number {
  return cart.reduce((s, i) => s + itemTotal(i), 0);
}

function cartCount(cart: CartItem[]): number {
  return cart.reduce((s, i) => s + (i.product.tipoVenta === "unidad" ? i.qty : 1), 0);
}

// ── Merchant personalization helpers ─────────────────────────────────────────

const MERCHANT_ZONE_NAMES: Record<string, Record<string, string>> = {
  tshirt: {
    "chest-front": "Pecho frente",
    "heart-area": "Área corazón",
    "chest-back": "Espalda",
    "sleeve-left": "Manga izquierda",
    "sleeve-right": "Manga derecha",
    "collar-exterior": "Cuello",
  },
  cap: {
    "front-panel": "Panel frontal",
    visor: "Visera",
    "side-left": "Lateral izquierdo",
    "side-right": "Lateral derecho",
    "back-strap": "Banda posterior",
  },
  jacket: {
    "chest-front": "Pecho frente",
    "chest-back": "Espalda",
    collar: "Cuello",
    "sleeve-left": "Manga izquierda",
    "sleeve-right": "Manga derecha",
  },
  apron: {
    chest: "Pecho",
    body: "Cuerpo",
    pocket: "Bolsillo",
  },
};

function inferGarmentKey(productName: string, categoryName?: string): string {
  const text = `${productName} ${categoryName ?? ""}`.toLowerCase();
  if (text.includes("gorra") || text.includes("cap") || text.includes("visera")) return "cap";
  if (text.includes("campera") || text.includes("jacket") || text.includes("buzo")) return "jacket";
  if (text.includes("delantal") || text.includes("apron")) return "apron";
  return "tshirt";
}

// Qué zonas pertenecen a cada vista (para filtrar el overlay correctamente)
const ZONE_VIEW_MAP: Record<string, Record<string, "front" | "back" | "both">> = {
  tshirt: {
    "chest-front": "front",
    "heart-area": "front",
    "chest-back": "back",
    "sleeve-left": "both",
    "sleeve-right": "both",
    "collar-exterior": "both",
  },
  jacket: {
    "chest-front": "front",
    "chest-back": "back",
    collar: "both",
    "sleeve-left": "both",
    "sleeve-right": "both",
  },
  cap: {
    "front-panel": "front",
    visor: "front",
    "side-left": "front",
    "side-right": "front",
    "back-strap": "front",
  },
  apron: { chest: "front", body: "front", pocket: "front" },
};

// Bounding boxes en coordenadas SVG para superponer la imagen subida sobre la prenda
const GARMENT_VIEWBOX: Record<string, { w: number; h: number }> = {
  tshirt: { w: 300, h: 400 },
  cap: { w: 300, h: 250 },
  jacket: { w: 300, h: 400 },
  apron: { w: 300, h: 400 },
};
const ZONE_BBOXES: Record<string, Record<string, { x: number; y: number; w: number; h: number }>> = {
  tshirt: {
    "chest-front": { x: 140, y: 150, w: 70, h: 80 },
    "heart-area": { x: 90, y: 140, w: 40, h: 35 },
    "chest-back": { x: 90, y: 130, w: 120, h: 150 },
    "sleeve-left": { x: 210, y: 42, w: 50, h: 78 },
    "sleeve-right": { x: 40, y: 42, w: 50, h: 78 },
    "collar-exterior": { x: 100, y: 40, w: 100, h: 25 },
  },
  cap: {
    "front-panel": { x: 100, y: 80, w: 100, h: 80 },
    visor: { x: 55, y: 178, w: 190, h: 32 },
    "side-left": { x: 195, y: 88, w: 63, h: 67 },
    "side-right": { x: 42, y: 88, w: 63, h: 67 },
    "back-strap": { x: 112, y: 171, w: 76, h: 12 },
  },
  jacket: {
    "chest-front": { x: 80, y: 130, w: 65, h: 110 },
    "chest-back": { x: 75, y: 120, w: 150, h: 180 },
    collar: { x: 95, y: 36, w: 110, h: 34 },
    "sleeve-left": { x: 218, y: 36, w: 52, h: 84 },
    "sleeve-right": { x: 30, y: 36, w: 52, h: 84 },
  },
  apron: {
    chest: { x: 102, y: 42, w: 96, h: 36 },
    body: { x: 72, y: 82, w: 156, h: 90 },
    pocket: { x: 110, y: 240, w: 80, h: 70 },
  },
};

// ── Page ─────────────────────────────────────────────────────────────────────

function StorefrontPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: "/market/$slug" });
  const catalogResult = Route.useLoaderData() as MarketCatalogResult;
  const error = catalogResult.ok ? null : catalogResult.error;
  const data = catalogResult.ok ? catalogResult.data : null;

  // LDP facetado: leer cat/marca del URL para inicializar el filtro inline
  const urlCatSlug = urlSearch.cat;
  const urlMarcaSlug = urlSearch.marca;

  // Catalog state
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [selectedMarca, setSelectedMarca] = useState<string | null>(null);
  const [storefrontOffers, setStorefrontOffers] = useState<
    Array<{
      id: string;
      kind: string;
      name: string;
      description: string;
      badge: string;
      title: string;
      body: string;
      imageUrl: string | null;
      showOnLdpFilter: boolean;
      productIds: string[];
      categoryIds: string[];
      appliesTo: string;
      code: string | null;
    }>
  >([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLabel, setCouponLabel] = useState<string | null>(null);
  const [couponApplying, setCouponApplying] = useState(false);
  const [search, setSearch] = useState("");
  const [productSort, setProductSort] = useState<ProductSortKey>(DEFAULT_PRODUCT_SORT);

  // Cart — hidratado post-mount; no escribir [] inicial (borra packs/productos en localStorage).
  const { cart, setCart } = usePersistedMarketCart<CartItem>(slug);
  const [addonProduct, setAddonProduct] = useState<Product | null>(null);
  const [customerLoggedIn, setCustomerLoggedIn] = useState(() => Boolean(getCustomerToken(slug)));

  useEffect(() => {
    setCustomerLoggedIn(Boolean(getCustomerToken(slug)));
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/market/${slug}/promotions`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setStorefrontOffers(Array.isArray(d.offers) ? d.offers : []);
      })
      .catch(() => {
        if (!cancelled) setStorefrontOffers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  const [cartOpen, setCartOpen] = useState(false);
  const [taxTotals, setTaxTotals] = useState<TaxBreakdownTotals | null>(null);
  useEffect(() => {
    if (!slug || !cart.length) {
      setTaxTotals(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      const items = cart.map((i) => ({
        productId: i.product.id,
        productName: i.product.name,
        tipoVenta: i.product.tipoVenta,
        cantidad: i.qty,
        unidadMedida: i.product.unidadMedida ?? "u",
        precioUnitario: i.variantPrecioUnitario ?? i.product.precioUnitario ?? undefined,
        precioPorKg: i.variantPrecioPorKg ?? i.product.precioPorKg ?? undefined,
        variantId: i.variantId ?? null,
        addons: i.addons?.map((a) => ({
          addonProductId: a.addonProductId,
          cantidad: a.qty,
        })),
      }));
      void fetch(`/api/market/${encodeURIComponent(slug)}/tax-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      })
        .then(async (res) => (res.ok ? res.json() : null))
        .then((data: { taxTotals?: TaxBreakdownTotals } | null) => {
          if (!cancelled) setTaxTotals(data?.taxTotals ?? null);
        })
        .catch(() => {
          if (!cancelled) setTaxTotals(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [slug, cart]);



  // Bottom nav tab activo
  const [bottomTab, setBottomTab] = useState<"home" | "search" | "categories" | "cart">("home");

  // Dine-In context (QR landing pasa ?table=&session=&mesa= a sessionStorage)
  const [dineInSessionId, setDineInSessionId] = useState<string | null>(null);
  const [dineInTableName, setDineInTableName] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tableToken = params.get("table");
    const sessionId = params.get("session");
    const mesa = params.get("mesa");
    if (tableToken) sessionStorage.setItem(`dineIn.${slug}.tableToken`, tableToken);
    if (mesa) {
      sessionStorage.setItem(`dineIn.${slug}.mesa`, mesa);
      setDineInTableName(mesa);
    } else {
      const storedMesa = sessionStorage.getItem(`dineIn.${slug}.mesa`);
      if (storedMesa) setDineInTableName(storedMesa);
    }
    if (sessionId) {
      sessionStorage.setItem(`dineIn.${slug}.sessionId`, sessionId);
      setDineInSessionId(sessionId);
    } else {
      const stored = sessionStorage.getItem(`dineIn.${slug}.sessionId`);
      if (stored) setDineInSessionId(stored);
    }
  }, [slug]);

  // Checkout
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<"info" | "personalize" | "success">("info");
  type ZonePData = { tipo: string; notas: string; fileName: string; fileUrl: string };
  type ItemPData = {
    garmentKey: string;
    view: "front" | "back";
    selectedZone: string | null;
    zones: Record<string, ZonePData>;
  };
  const [personalization, setPersonalization] = useState<Record<string, ItemPData>>({});
  const fileInputsRef = useRef<Map<string, HTMLInputElement>>(new Map());
  const [checkoutFieldErrors, setCheckoutFieldErrors] = useState<MarketCheckoutFieldErrors>({});
  const [placing, setPlacing] = useState(false);
  const checkoutIdempotencyKeyRef = useRef<string | null>(null);
  const merchantPersonalizationNotesRef = useRef<string>("");
  // Descuentos (Fase D)
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [loyalty, setLoyalty] = useState<{
    enabled: boolean;
    balance: number;
    redeemRate: number;
    minRedeem: number;
  } | null>(null);
  const [orderResult, setOrderResult] = useState<{
    id: string;
    paymentMethod?: string;
    bankTransfer?: BankTransferInfo | null;
    cancelToken?: string | null;
    paymentLink?: string | null;
    fulfillmentMode?: string;
    scheduledFor?: string | null;
  } | null>(null);

  const [selectedShipping, setSelectedShipping] = useState<
    import("@/components/market/MarketShippingQuote").MarketShippingOption | null
  >(null);
  const [checkoutPrefill, setCheckoutPrefill] = useState<MarketCheckoutPrefill | null>(null);
  const [paymentChangeNotice, setPaymentChangeNotice] = useState<string | null>(null);

  // Checkout form
  const [form, setForm] = useState(() => {
    const initial = {
      name: "",
      email: "",
      phone: "",
      metodEntrega: "retiro" as "domicilio" | "retiro",
      locationId: "",
      direccionEntrega: "",
      codigoPostal: "",
      paymentMethod: "efectivo",
      notes: "",
    };
    if (!data) return initial;
    const metodEntrega =
      data.config.metodEntrega !== "ambos"
        ? (data.config.metodEntrega as "domicilio" | "retiro")
        : initial.metodEntrega;
    const def = data.locations.find((l) => l.isDefault) ?? data.locations[0];
    const methods = data.config.paymentMethods?.length
      ? data.config.paymentMethods
      : ["efectivo", "transferencia"];
    return {
      ...initial,
      metodEntrega,
      locationId: def?.id ?? "",
      paymentMethod: methods[0] ?? "efectivo",
    };
  });
  const prevMetodEntregaRef = useRef(form.metodEntrega);

  // Pedido desde mesa QR → siempre "retiro" (consumo en salón)
  useEffect(() => {
    if (!dineInSessionId) return;
    setForm((f) => (f.metodEntrega === "retiro" ? f : { ...f, metodEntrega: "retiro" }));
  }, [dineInSessionId]);

  const updateCheckoutForm = useCallback((updater: SetStateAction<typeof form>) => {
    setCheckoutFieldErrors({});
    setForm(updater);
  }, []);

  const goBackToCart = useCallback(() => {
    setCheckoutOpen(false);
    setCheckoutStep("info");
    setCartOpen(true);
  }, []);

  useEffect(() => {
    if (!data) return;
    const seo = data.config.seo;
    const title = seo?.title?.trim() || data.tenant.name;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const desc = seo?.description?.trim() || data.config.heroSubtitle || data.tenant.description || "";
    if (desc) meta.setAttribute("content", desc);
    const productImgs = (data.products ?? [])
      .map((p) => p.imagenUrl)
      .filter(Boolean)
      .slice(0, 8) as string[];
    const shareImage = resolveTenantShareImage({
      seoOgImageUrl: seo?.ogImageUrl,
      logoUrl: data.tenant.logoUrl,
      heroImageUrl: data.config.heroImageUrl,
      productImageUrls: productImgs,
      slug,
      origin: window.location.origin,
    });
    const upsertMeta = (attr: "property" | "name", key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    upsertMeta("property", "og:site_name", title);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:image", shareImage);
    upsertMeta("name", "twitter:image", shareImage);
    upsertMeta("name", "twitter:title", title);

    // PWA branding por tenant: manifest dinámico + theme-color + apple-touch-icon.
    const upsertLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    const iconHref = data.tenant.logoUrl?.trim() || tenantBrandIconUrl(slug, window.location.origin);
    upsertLink("manifest", `/api/manifest/${slug}`);
    upsertLink("icon", iconHref);
    upsertLink("apple-touch-icon", iconHref);
    const theme = data.config.primaryColor;
    if (theme && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(theme)) {
      let tc = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
      if (!tc) {
        tc = document.createElement("meta");
        tc.setAttribute("name", "theme-color");
        document.head.appendChild(tc);
      }
      tc.setAttribute("content", theme);
    }
    const appleTitle =
      document.querySelector('meta[name="apple-mobile-web-app-title"]') ??
      (() => {
        const m = document.createElement("meta");
        m.setAttribute("name", "apple-mobile-web-app-title");
        document.head.appendChild(m);
        return m;
      })();
    appleTitle.setAttribute("content", data.tenant.name);
  }, [data, slug]);

  // ── Cart operations ──────────────────────────────────────────────────────────
  const commitCartLine = useCallback((line: CartItem) => {
    setCart((prev) => {
      const lk = cartLineKey(line);
      const limit = cartLineQtyLimit(line);
      const existing = prev.find((i) => cartLineKey(i) === lk);
      if (existing) {
        return prev.map((i) =>
          cartLineKey(i) === lk ? { ...i, qty: Math.min(i.qty + line.qty, limit) } : i,
        );
      }
      return [...prev, { ...line, qty: Math.min(line.qty, limit) }];
    });
    trackAddToCart(slug, { id: line.product.id, name: line.product.name, slug: line.product.slug }, line.qty);
    showAddedToCart({
      title: line.product.name,
      storeSlug: slug,
      imageUrl: line.product.imagenUrl,
      qty: line.qty,
    });
  }, [slug]);

  const addToCart = useCallback((product: Product, qty = 1) => {
    if (productNeedsAddonPrompt(product)) {
      // Una unidad por confirmación — así cada hamburguesa puede llevar extras distintos.
      setAddonProduct(product);
      return;
    }
    commitCartLine({ product, qty, addons: [] });
  }, [commitCartLine]);

  const onAddonConfirm = useCallback((payload: AddonConfirmPayload) => {
    commitCartLine({ product: payload.product, qty: 1, addons: payload.addons });
    setAddonProduct(null);
  }, [commitCartLine]);

  const updateQty = useCallback((lineKey: string, delta: number) => {
    setCart((prev) => {
      if (delta < 0) {
        const line = prev.find((i) => cartLineKey(i) === lineKey);
        if (line) {
          trackRemoveFromCart(
            slug,
            { id: line.product.id, name: line.product.name, slug: line.product.slug },
            Math.min(line.qty, Math.abs(delta)),
          );
        }
      }
      return prev
        .map((i) =>
          cartLineKey(i) === lineKey
            ? { ...i, qty: Math.min(Math.max(0, i.qty + delta), cartLineQtyLimit(i)) }
            : i,
        )
        .filter((i) => i.qty > 0);
    });
  }, [slug]);

  const removeFromCart = useCallback((lineKey: string) => {
    setCart((prev) => {
      const line = prev.find((i) => cartLineKey(i) === lineKey);
      if (line) {
        trackRemoveFromCart(
          slug,
          { id: line.product.id, name: line.product.name, slug: line.product.slug },
          line.qty,
        );
      }
      return prev.filter((i) => cartLineKey(i) !== lineKey);
    });
  }, [slug]);

  /** Quita 1 unidad de cualquier línea del producto (última primero). */
  const decrementProduct = useCallback((productId: string) => {
    setCart((prev) => {
      const idxs = prev.map((l, i) => (l.product.id === productId ? i : -1)).filter((i) => i >= 0);
      if (!idxs.length) return prev;
      const i = idxs[idxs.length - 1];
      const line = prev[i];
      trackRemoveFromCart(slug, { id: line.product.id, name: line.product.name, slug: line.product.slug }, 1);
      if (line.qty <= 1) return prev.filter((_, j) => j !== i);
      return prev.map((l, j) => (j === i ? { ...l, qty: l.qty - 1 } : l));
    });
  }, [slug]);

  // Para el badge en el grid: suma de todas las líneas (base + variantes) del producto.
  const qtyInCart = (productId: string) =>
    cart.filter((i) => i.product.id === productId).reduce((s, i) => s + i.qty, 0);

  // Clave de la línea "base" (sin variante ni addons) — para el +/- del grid sin extras.
  const baseLineKey = (product: Product) => cartLineKey({ product });

  // ¿El producto exige pasar por la PDP? (variantes / gift card / quick_add off).
  // Los addons se configuran en sheet al agregar (una unidad por vez).
  // quickAdd viene de doc.commercePresentation (TenantExperience) — mismo campo que
  // ya consumen MarketPlpPage/MarketLandingPage, no el flag legacy features.quick_add.
  const experienceDoc = data?.experiencePublished ?? null;
  const requiresDetail = (product: Product) =>
    isGiftCardProduct(product) ||
    !(experienceDoc?.commercePresentation?.quickAdd ?? true) ||
    (product.variants?.length ?? 0) > 0 ||
    (product.variantAxes?.length ?? 0) > 0;

  // Autocompletar checkout con datos del cliente logueado.
  useEffect(() => {
    if (!checkoutOpen) {
      setCheckoutPrefill(null);
      return;
    }
    const profile = getCustomerProfile(slug);
    if (!profile) return;
    const base: MarketCheckoutPrefill = {
      name: profile.name,
      email: profile.email,
      phone: profile.phone ?? "",
    };
    setCheckoutPrefill(base);
    void customerFetch(slug, `/api/market/${slug}/account/addresses`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { addresses?: Record<string, unknown>[] } | null) => {
        if (!d?.addresses?.length) return;
        const def = d.addresses.find((a) => a.isDefault) ?? d.addresses[0];
        setCheckoutPrefill((p) => ({
          ...(p ?? base),
          deliveryAddress: normalizedAddressFromDb(def),
        }));
      })
      .catch(() => {});
  }, [checkoutOpen, slug]);

  // Cargar puntos del cliente al abrir el checkout (si está logueado).
  useEffect(() => {
    if (!checkoutOpen || !getCustomerToken(slug)) {
      setLoyalty(null);
      return;
    }
    void customerFetch(slug, `/api/market/${slug}/account/loyalty`)
      .then((r) => r.json())
      .then((d) => {
        if (d.enabled)
          setLoyalty({
            enabled: true,
            balance: d.balance ?? 0,
            redeemRate: d.config?.redeemRate ?? 1,
            minRedeem: d.config?.minRedeem ?? 100,
          });
        else setLoyalty(null);
      })
      .catch(() => setLoyalty(null));
  }, [checkoutOpen, slug]);

  const applyGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    try {
      const r = await fetch(`/api/market/${slug}/giftcard?code=${encodeURIComponent(giftCardCode.trim())}`);
      const d = await r.json();
      if (d.valid) {
        setGiftCardBalance(d.balance);
        toast.success(`Gift card válida: ${pesos(d.balance)}`);
      } else {
        setGiftCardBalance(null);
        toast.error(d.error ?? "Gift card inválida");
      }
    } catch {
      toast.error("No se pudo validar la gift card");
    }
  };

  // El Runtime solo avisa que se eligió una promoción (D4B): precargar el
  // código y avisar dónde usarlo sigue siendo lógica de checkout de la ruta.
  const applyPromotionCoupon = (offer: StorefrontOffer) => {
    if (!offer.code) return;
    setCouponCode(offer.code);
    toast.message(`Usá el código ${offer.code} en el checkout`);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponApplying(true);
    try {
      const items = cart.map((i) => ({
        productId: i.product.id,
        categoryId: i.product.categoriaId ?? null,
        qty: i.qty,
        priceCents: marketLineUnitCents(i),
      }));
      const totalCents = Math.round(cartTotal(cart) * 100);
      const r = await fetch(`/api/market/${slug}/coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: couponCode.trim(),
          items,
          totalCents,
          email: form.email.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (d.ok) {
        setCouponDiscount((d.discountCents ?? 0) / 100);
        setCouponLabel(d.discountLabel ?? couponCode.trim().toUpperCase());
        toast.success(d.discountLabel ?? "Cupón aplicado");
      } else {
        setCouponDiscount(0);
        setCouponLabel(null);
        toast.error(d.error ?? "Cupón inválido");
      }
    } catch {
      toast.error("No se pudo validar el cupón");
    } finally {
      setCouponApplying(false);
    }
  };

  // Descuento estimado (gift card primero, luego puntos sobre el remanente).
  const totalCartForDiscount = cartTotal(cart);
  const giftDiscount = giftCardBalance != null ? Math.min(giftCardBalance, totalCartForDiscount) : 0;
  const afterGift = Math.max(0, totalCartForDiscount - giftDiscount);
  const pointsDiscount =
    loyalty && redeemPoints >= loyalty.minRedeem ? Math.min(redeemPoints * loyalty.redeemRate, afterGift) : 0;
  const bundleDiscount = cartBundleDiscountTotal(cart);
  const discountTotal = Math.round((giftDiscount + pointsDiscount + couponDiscount + bundleDiscount) * 100) / 100;
  const giftCardsCheckoutEnabled = data?.giftCardsEnabled === true;
  const showLoyaltyRedeem = !!(loyalty?.enabled && loyalty.balance >= loyalty.minRedeem);
  const showCheckoutDiscounts = true; // siempre mostrar cupón (+ gift/loyalty si aplica)

  // Nota: el checkout real vive en /market/:slug/checkout (MarketCheckoutPage) desde la
  // migración a ruta dedicada. handlePlaceOrder/handleConfirmPersonalization (el flujo de
  // Dialog embebido pre-migración) se eliminaron acá — sin call sites, checkoutOpen nunca
  // pasaba a true, así que nunca se ejecutaban.

  const promoProductIds = useMemo(() => {
    const ids = new Set<string>();
    const catIds = new Set<string>();
    for (const o of storefrontOffers) {
      if (!o.showOnLdpFilter) continue;
      if (o.appliesTo === "all") return { all: true as const, ids, catIds };
      for (const id of o.productIds ?? []) ids.add(id);
      for (const id of o.categoryIds ?? []) catIds.add(id);
    }
    return { all: false as const, ids, catIds };
  }, [storefrontOffers]);

  const productInPromo = useCallback(
    (p: { id: string; categoriaId: string | null }) => {
      if (promoProductIds.all) return true;
      if (promoProductIds.ids.has(p.id)) return true;
      if (p.categoriaId && promoProductIds.catIds.has(p.categoriaId)) return true;
      return false;
    },
    [promoProductIds],
  );

  const filteredProducts = useMemo(() => {
    const filtered = (data?.products ?? []).filter((p) => {
      const matchCat = !selectedCat
        ? true
        : selectedCat === PROMO_FILTER
          ? productInPromo(p)
          : p.categoriaId === selectedCat;
      // LDP facetado: filtro de marca inline
      const matchMarca = !selectedMarca ? true : p.brandName?.toLowerCase() === selectedMarca.toLowerCase();
      // brandName entra al match para que la franja de marcas (Fase 3) filtre.
      const matchSearch = matchesSearch(
        search,
        p.name,
        p.description,
        p.categoriaName,
        p.brandName,
        p.tags.join(" "),
      );
      return matchCat && matchMarca && matchSearch;
    });
    return sortProducts<Product>(filtered, productSort);
  }, [data?.products, selectedCat, selectedMarca, search, productSort, productInPromo]);

  const carruselSections = useMemo(() => {
    if (!data) return [];
    const list = data.config.carruselesActivos ?? [];
    return [...list]
      .sort((a, b) => a.order - b.order)
      .map((c) => ({
        ...c,
        products: data.products.filter((p) => p.tags.includes(c.tag)).slice(0, 12),
      }))
      .filter((s) => s.products.length > 0);
  }, [data]);

  const hasActiveFilters = marketHasActiveFilters(
    search,
    selectedCat === PROMO_FILTER ? "promo" : selectedCat,
    productSort,
  );
  /** Home nunca entra en modo SPA/catálogo. Búsqueda → PLP. */
  const isBrowsingFiltered = false;

  // Composición por perfil de negocio — resuelta server-side vía Experience
  // Engine (Fase D1, ver ADR-002). Navigation Renaissance: Home nunca embebe
  // el catálogo completo (vive en LDP/PLP).
  const composition = useMemo(() => {
    const base = data?.composition ?? EMPTY_STOREFRONT_COMPOSITION;
    return {
      ...base,
      sections: base.sections.filter((section) => section.kind !== "catalog"),
    };
  }, [data?.composition]);

  // Conteo de productos por categoría (para pills y sidebar)
  const productCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of data?.products ?? []) {
      if (p.categoriaId) counts[p.categoriaId] = (counts[p.categoriaId] ?? 0) + 1;
    }
    return counts;
  }, [data?.products]);

  const clearFilters = useCallback(() => {
    setSearch("");
    setSelectedCat(null);
    setSelectedMarca(null);
    setProductSort(DEFAULT_PRODUCT_SORT);
    void navigate({ to: "/market/$slug", params: { slug }, search: { cat: undefined, marca: undefined }, replace: true });
  }, [navigate, slug]);

  const cartPrepMinutes = useMemo(
    () =>
      cart.reduce((max, line) => {
        const p = data?.products.find((x) => x.id === line.product.id);
        return Math.max(max, p?.tiempoPreparacionMinutos ?? 0);
      }, 0),
    [cart, data?.products],
  );

  const checkoutPickupLocations = useMemo(() => {
    if (!data) return [];
    return buildCheckoutPickupLocations(data.locations, data.tenant);
  }, [data]);

  const pickupAvailable = checkoutPickupLocations.length > 0;

  const checkoutMetodEntregaConfig = useMemo(() => {
    if (!data) return "ambos" as const;
    return resolveCheckoutMetodEntregaConfig(
      data.config.metodEntrega,
      pickupAvailable,
      data.config.deliveryConfigured === true,
    );
  }, [data, pickupAvailable]);

  useEffect(() => {
    if (!data || !pickupAvailable) {
      if (data && form.metodEntrega === "retiro" && data.config.metodEntrega === "ambos") {
        setForm((f) => ({ ...f, metodEntrega: "domicilio", locationId: "" }));
      }
      return;
    }
    if (form.metodEntrega === "retiro") {
      const valid = checkoutPickupLocations.some((l) => l.id === form.locationId);
      if (!valid) {
        setForm((f) => ({
          ...f,
          locationId: defaultCheckoutPickupLocationId(checkoutPickupLocations),
        }));
      }
    }
  }, [data, pickupAvailable, checkoutPickupLocations, form.metodEntrega, form.locationId]);

  const availabilityLocationId = useMemo(() => {
    if (!data) return "";
    if (form.metodEntrega === "retiro") {
      return resolveOrderPickupLocationId(form.locationId) ?? "";
    }
    const def = checkoutPickupLocations.find((l) => !l.isBusinessFallback) ?? checkoutPickupLocations[0];
    return resolveOrderPickupLocationId(def?.id) ?? "";
  }, [data, form.metodEntrega, form.locationId, checkoutPickupLocations]);

  const {
    evaluation: fulfillmentEval,
    loading: fulfillmentLoading,
    error: fulfillmentError,
  } = useMarketAvailability({
    slug,
    metodEntrega: form.metodEntrega,
    locationId: availabilityLocationId,
    cartPrepMinutes,
    enabled: checkoutOpen && checkoutStep === "info",
  });

  const checkoutBlocked = fulfillmentEval?.enabled === true && fulfillmentEval.canPlaceOrder === false;

  const resolvedDeliveryForPayment: "domicilio" | "retiro" = useMemo(() => {
    const met = checkoutMetodEntregaConfig;
    if (met === "domicilio") return "domicilio";
    if (met === "retiro") return "retiro";
    return form.metodEntrega;
  }, [checkoutMetodEntregaConfig, form.metodEntrega]);

  const paymentOptions = useMemo(() => {
    if (!data) return [];
    const bt = data.config.bankTransfer;
    return filterPaymentMethodsForDelivery(
      buildCheckoutPaymentOptions({
        tenantMethods: data.config.paymentMethods ?? [],
        mpEnabled: data.config.mpEnabled,
        bankTransferConfigured: Boolean(bt?.cbu || bt?.alias || bt?.holder),
        onlinePaymentMethodIds: data.config.onlinePaymentMethodIds ?? [],
      }),
      resolvedDeliveryForPayment,
    );
  }, [data, resolvedDeliveryForPayment]);

  useEffect(() => {
    if (prevMetodEntregaRef.current === form.metodEntrega) return;
    prevMetodEntregaRef.current = form.metodEntrega;
    setSelectedShipping(null);
    if (paymentOptions.length > 0 && form.paymentMethod && !paymentOptions.includes(form.paymentMethod)) {
      setPaymentChangeNotice(
        `Cambiaste a ${form.metodEntrega === "domicilio" ? "envío a domicilio" : "retiro en local"}. «${checkoutPaymentLabel(form.paymentMethod)}» ya no está disponible. Elegí cómo querés pagar.`,
      );
    }
  }, [form.metodEntrega, form.paymentMethod, paymentOptions]);

  const cartNeedsShipping = useMemo(
    () => cartRequiresShipping(cart.map((i) => ({ productType: i.product.productType ?? "physical" }))),
    [cart],
  );

  const shippingCartItems = useMemo(
    () =>
      cart
        .filter((i) => productTypeRequiresShipping(i.product.productType ?? "physical"))
        .map((i) => ({
          productId: i.product.id,
          quantity: i.qty,
          tipoVenta: i.product.tipoVenta,
        })),
    [cart],
  );

  const offlineScreen = renderMarketPublicOffline(catalogResult);
  if (offlineScreen) return offlineScreen;

  // ── Error ───────────────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center p-6">
        <Package className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no encontrada</h1>
        <p className="text-muted-foreground">{error ?? "Esta tienda no existe o no está disponible."}</p>
      </div>
    );
  }

  const { tenant, config, categories, locations } = data;
  const mainLocation = locations.find((l) => l.isDefault) ?? locations[0];
  // Ola 1.2 — navegación rica del header: categorías/marcas/colecciones ya
  // resuelven a su URL de landing propia (`/market/:slug/categoria|marca|coleccion/:target`).
  type CatWithExp = Category & { experience: CategoryExperience };
  const headerCategories = filterCategoriesForNav(
    categories.map((c: Category): CatWithExp => ({
      ...c,
      experience: c.experience ?? DEFAULT_CATEGORY_EXPERIENCE,
    })),
    "header",
  )
    .slice()
    .sort((a: CatWithExp, b: CatWithExp) => Number(b.experience.featured) - Number(a.experience.featured) || a.sortOrder - b.sortOrder)
    .map((c: CatWithExp) => ({
      id: c.id,
      name: c.name,
      href: landingPath(slug, "category", c.slug),
    }));
  const headerBrands = Array.from(
    new Set(data.products.map((p: Product) => p.brandName).filter(Boolean) as string[]),
  )
    .slice(0, 24)
    .map((name: string) => ({ name, href: landingPath(slug, "brand", slugifyName(name)) }));
  const headerCollections = Array.from(new Set(data.products.flatMap((p: Product) => p.tags) as string[]))
    .slice(0, 16)
    .map((tag: string) => ({ name: tag, href: landingPath(slug, "collection", slugifyName(tag)) }));
  const totalCart = cartTotal(cart);
  const countCart = cartCount(cart);
  const heroTitle = (config.heroTitle ?? tenant.name).trim();
  const heroSubtitle = (config.heroSubtitle ?? tenant.description ?? "").trim();
  const showHeroSubtitle = Boolean(heroSubtitle) && heroSubtitle.toLowerCase() !== heroTitle.toLowerCase();

  // Tokens de theme resueltos server-side vía Experience Engine (Fase D1).
  // Antes de D1 se calculaba acá mismo con marketSurfaceStyle(); mismo resultado.
  const brandStyle = data.brand.surfaceStyle;
  
  // Sincronizar selectedCat/selectedMarca desde URL params (LDP facetado)
  useEffect(() => {
    if (!data) return;
    if (urlCatSlug) {
      const cat = data.categories.find((c: Category) => c.slug === urlCatSlug);
      if (cat) setSelectedCat(cat.id);
    }
    if (urlMarcaSlug) setSelectedMarca(urlMarcaSlug);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, urlCatSlug, urlMarcaSlug]);

  const goCategory = useCallback(
    (id: SetStateAction<string | null>) => {
      const next = typeof id === "function" ? id(selectedCat) : id;
      if (!next) {
        setSelectedCat(null);
        void navigate({ to: "/market/$slug", params: { slug }, search: { cat: undefined, marca: undefined }, replace: true });
        return;
      }
      const cat = categories.find((c: Category) => c.id === next);
      if (cat?.slug) {
        setSelectedCat(next);
        void navigate({ to: "/market/$slug", params: { slug }, search: { cat: cat.slug, marca: undefined }, replace: true });
      }
    },
    [categories, navigate, selectedCat, slug],
  );

  const goToPlp = useCallback(
    (opts?: { q?: string }) => {
      navigate({
        to: "/market/$slug/catalogo",
        params: { slug },
        search: { q: opts?.q || undefined },
      });
    },
    [navigate, slug],
  );

  // Home search: navegación a PLP se dispara solo desde Enter u onOpenSearch.

const experienceProjection = (() => {
    try {
      return data.experiencePublished ? projectExperience(data.experiencePublished as never) : null;
    } catch {
      return null;
    }
  })();
  // SCRUM-342: el modo de tema y el toggle al comprador ahora se aplican de verdad.
  const storefrontTheme = useStorefrontTheme(slug, experienceProjection?.theme ?? null);
  const effectiveBrandStyle =
    storefrontTheme.themeStyle ?? experienceProjection?.brandStyle ?? brandStyle;

  // Contexto del Storefront Runtime (Storefront Engine V2, D1) — todo lo que
  // los renderers de sección necesitan leer o disparar. La ruta sigue
  // dueña del carrito/checkout; el Runtime solo interpreta `composition`.
  const runtimeCtx: StorefrontRuntimeContext = {
    identity: {
      slug,
      tenant,
      storeInfo: {
        metodEntrega: config.metodEntrega,
        whatsappNumber: config.whatsappNumber,
        showAddressOnStorefront: config.showAddressOnStorefront !== false,
      },
      locations,
      mainLocation,
      socialLinks: data.socialLinks,
    },
    catalog: {
      categories,
      products: data.products,
      combos: data.combos,
      bundles: data.bundles,
      offers: storefrontOffers,
      onPromotionSelected: applyPromotionCoupon,
    },
    hero: {
      title: experienceProjection?.hero.title ?? heroTitle,
      subtitle: experienceProjection?.hero.subtitle ?? heroSubtitle,
      showSubtitle: experienceProjection?.hero.showSubtitle ?? showHeroSubtitle,
      imageUrl: experienceProjection?.hero.imageUrl ?? config.heroImageUrl,
      videoUrl: experienceProjection?.hero.videoUrl ?? config.heroVideoUrl,
      slides: experienceProjection?.hero.slides.map((s) => ({
        id: s.id,
        imageUrl: s.media?.kind === "image" ? s.media.src : null,
        videoUrl: s.media?.kind === "video" ? s.media.src : null,
        title: s.headline,
        subtitle: s.subheadline,
        ctaLabel: s.cta1?.label,
        cta2Label: s.cta2?.label,
        overlayOpacity: s.overlay?.opacity,
        overlayColor: s.overlay?.color,
        alignment: s.alignment,
        badge: s.badge,
        countdownEndsAt: s.countdown?.endsAt,
        contentPosition: s.contentPosition,
        visibleDesktop: s.visibility?.desktop,
        visibleMobile: s.visibility?.mobile,
        scheduleStart: s.schedule?.start,
        scheduleEnd: s.schedule?.end,
      })),
      autoplay: experienceProjection?.hero.autoplay,
      intervalMs: experienceProjection?.hero.intervalMs,
      showDots: experienceProjection?.hero.showDots,
      showArrows: experienceProjection?.hero.showArrows,
      height: experienceProjection?.hero.height,
    },
    filters: {
      search,
      setSearch,
      selectedCat,
      setSelectedCat: goCategory,
      productSort,
      setProductSort,
      filteredProducts,
      hasActiveFilters,
      clearFilters,
      isBrowsingFiltered,
    },
    cart: {
      addToCart,
      qtyInCart,
      updateQty,
      decrementProduct,
      baseLineKey,
      requiresDetail,
    },
    experience: experienceProjection ? { plp: experienceProjection.plp } : undefined,
  };

  return (
    <div className="market-surface min-h-screen" style={effectiveBrandStyle}>
      <MarketEngagementOverlays slug={slug} data={data} />
      <MarketTrackingScripts tracking={config.tracking} slug={slug} />
      {experienceProjection?.announcement ? (
        <MarketAnnouncementBar announcement={experienceProjection.announcement} storeSlug={slug} />
      ) : null}
      <MarketStoreHeader
        tenantName={tenant.name}
        logoUrl={tenant.logoUrl}
        cartCount={countCart}
        cartTotal={totalCart}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
        }}
        accountHref={data.customerAccountsEnabled ? storefrontPath(slug, "/cuenta") : undefined}
        onOpenCart={() => setCartOpen(true)}
        onOpenSearch={() => {
          navigate({ to: "/market/$slug/catalogo", params: { slug }, search: { q: search || undefined } });
        }}
        onAddProductById={(id) => {
          const product = data.products.find((p) => p.id === id);
          if (product) addToCart(product, 1);
        }}
        qtyInCartById={qtyInCart}
        experience={experienceProjection?.header}
        navigation={data.experiencePublished?.navigation}
        categories={headerCategories}
        brands={headerBrands}
        collections={headerCollections}
        storeSlug={slug}
        showBundles={(data.bundles?.length ?? 0) > 0}
        showGiftCards={data.giftCardsEnabled === true && data.products.some(isGiftCardProduct)}
            showReservations={data.showReservations}
            showServiceBookings={data.showServiceBookings}
        showB2bPortal={data.showB2bPortal}
        activeB2bChannel={data.activeB2bChannel}
        themeStyle={effectiveBrandStyle}
        themeAppearance={storefrontTheme.appearance}
        onToggleTheme={storefrontTheme.canToggle ? storefrontTheme.toggle : undefined}
        onSelectCategory={(id) => {
          goCategory(id);
        }}
      />

      {/* Home: sin sidebar de catálogo. La PLP vive en /catalogo. */}
      <div className="flex min-h-screen">
        <main className="min-w-0 flex-1 pb-20 md:pb-8">
          {/* Único h1 de la página: los perfiles sin hero no tenían ninguno */}
          <h1 className="sr-only">{tenant.name}</h1>

          {dineInSessionId ? (
            <div className="sticky top-14 z-30 md:top-16 border-b border-emerald-500/30 bg-emerald-600 text-white px-4 py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">
                  Pedido en mesa{dineInTableName ? ` · ${dineInTableName}` : ""}
                </p>
                <p className="text-[11px] text-white/85">
                  Elegí del menú y confirmá — la orden llega a cocina
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCartOpen(true);
                  setBottomTab("cart");
                }}
                className="flex-shrink-0 rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/25"
              >
                Ver pedido
              </button>
            </div>
          ) : null}

          {/* ── Disposición de componentes por nicho — Storefront Runtime (D1) ── */}
          <StorefrontRuntime composition={composition} ctx={runtimeCtx} />
          <ProductAddonsSheet
            open={!!addonProduct}
            product={addonProduct}
            onClose={() => setAddonProduct(null)}
            onConfirm={onAddonConfirm}
          />

          {/* Captación de cuenta: fuera del fold, como cierre — no como peaje de entrada */}
          {data.customerAccountsEnabled && !customerLoggedIn ? (
            <MarketCustomerAccountBanner
              slug={slug}
              tenantName={tenant.name}
              variant={
                data.showServiceBookings
                  ? data.products.length > 0
                    ? "mixto"
                    : "servicios"
                  : "commerce"
              }
            />
          ) : null}

          <MarketFooter
            tenantName={tenant.name}
            description={tenant.description}
            logoUrl={tenant.logoUrl}
            phone={tenant.phone}
            whatsappNumber={config.whatsappNumber}
            locations={locations}
            categories={data.categories}
            socialLinks={data.socialLinks}
            footer={data.experiencePublished?.footer}
            storeSlug={slug}
            showReservations={data.showReservations}
            showServiceBookings={data.showServiceBookings}
        showB2bPortal={data.showB2bPortal}
        activeB2bChannel={data.activeB2bChannel}
            showMap={data.experiencePublished?.footer.showMap ?? true}
          />
        </main>
      </div>

      {/* ── Bottom nav mobile ── */}
      <MarketBottomNav
        activeTab={bottomTab}
        cartCount={countCart}
        cartTotal={totalCart}
        onTabChange={(tab) => {
          if (tab === "cart") {
            setCartOpen(true);
            setBottomTab("home");
          } else if (tab === "search") {
            setBottomTab("search");
            navigate({ to: "/market/$slug/catalogo", params: { slug }, search: {} });
          } else if (tab === "categories") {
            setBottomTab("categories");
            document.getElementById("categorias")?.scrollIntoView({ behavior: "smooth" });
          } else {
            setBottomTab("home");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
      />

      {/* ── Floating cart bar desktop only ── */}
      {cart.length > 0 && !cartOpen && (
        <div className="hidden md:block">
          <MarketFloatingCartBar
            cartCount={countCart}
            cartTotal={totalCart}
            onOpenCart={() => setCartOpen(true)}
          />
        </div>
      )}

      {/* ── WhatsApp FAB — sube sobre el bottom nav en mobile ── */}
      {config.whatsappNumber && (
        <a
          href={`https://wa.me/${config.whatsappNumber.replace(/\D/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-20 right-4 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-[#25d366] text-white shadow-lg transition-transform hover:scale-105 md:bottom-6 md:h-12 md:w-12"
          aria-label="WhatsApp"
        >
          <MessageCircle className="h-5 w-5 md:h-6 md:w-6" />
        </a>
      )}

      {/* ── Cart drawer ── */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent
          side="right"
          style={effectiveBrandStyle}
          className="market-surface flex h-[100dvh] w-full max-w-none flex-col border-0 p-0 !inset-0 sm:!inset-y-0 sm:!left-auto sm:!right-0 sm:h-full sm:max-w-[420px] sm:border-l"
        >
          <SheetHeader className="border-b px-4 py-4">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Carrito
              {cart.length > 0 && (
                <Badge variant="secondary">
                  {countCart} {countCart === 1 ? "ítem" : "ítems"}
                </Badge>
              )}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Productos agregados al pedido de la tienda.
            </SheetDescription>
          </SheetHeader>

          {cart.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center text-muted-foreground">
              <ShoppingCart className="h-12 w-12 opacity-25" />
              <div>
                <p className="text-base font-medium text-[var(--market-on-surface)]">Tu carrito está vacío</p>
                <p className="mt-1 text-sm">Agregá productos para empezar tu pedido.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setCartOpen(false)}>
                Seguir comprando
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto divide-y">
                {cart.map((item) => {
                  const lk = cartLineKey(item);
                  return (
                    <MarketCartItemRow
                      key={lk}
                      item={item}
                      onIncrement={() => updateQty(lk, item.product.tipoVenta === "peso" ? 0.1 : 1)}
                      onDecrement={() => updateQty(lk, item.product.tipoVenta === "peso" ? -0.1 : -1)}
                      onRemove={() => removeFromCart(lk)}
                    />
                  );
                })}
              </div>

              {/* Cart footer — resumen comercial V2 */}
              <div className="border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3">
                <MarketOrderTotals
                  subtotal={taxTotals?.grossAmount ?? totalCart}
                  discountTotal={discountTotal}
                  bundleDiscount={bundleDiscount}
                  taxTotals={taxTotals}
                  shippingPending={cartNeedsShipping && config.metodEntrega !== "retiro"}
                  shippingLabel={config.metodEntrega === "retiro" ? "retiro" : undefined}
                  total={computeOrderTotal(taxTotals?.grossAmount ?? totalCart, discountTotal, 0)}
                  compact
                />
                {cart.some((i) => i.product.tipoVenta === "peso") && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                    <Scale className="h-3 w-3" />
                    Los productos a peso muestran precio estimado. El total final se ajustará al pesar.
                  </p>
                )}
                <Button
                  className="market-accent-glow h-12 w-full bg-[var(--market-accent)] text-base font-bold text-[var(--primary-foreground)] hover:opacity-90"
                  onClick={() => {
                    setCartOpen(false);
                    void navigate({ to: "/market/$slug/checkout", params: { slug } });
                  }}
                >
                  Hacer pedido · {pesos(computeOrderTotal(taxTotals?.grossAmount ?? totalCart, discountTotal, 0))}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

          </div>
  );
}
